/**
 * admin/src/pages/EmailTemplatesPage.tsx
 * Quản lý Email Templates — lưu localStorage
 * CRUD: Tạo, sửa, xóa, dùng template
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, toast } from '../components/ui/index';

// ─── Types ────────────────────────────────────────────────────────────────────

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  mode: 'plain' | 'html';
  createdAt: number;
  updatedAt: number;
}

// ─── Storage ──────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'admin-email-templates';

function loadTemplates(): EmailTemplate[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as EmailTemplate[]) : [];
  } catch {
    return [];
  }
}

function saveTemplates(templates: EmailTemplate[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
}

// ─── Starter templates ────────────────────────────────────────────────────────

const STARTER_TEMPLATES: Omit<EmailTemplate, 'id' | 'createdAt' | 'updatedAt'>[] = [
  {
    name: '🎉 Chào mừng Premium',
    subject: 'Chúc mừng bạn đã nâng cấp lên Premium!',
    body: 'Chào {{name}},\n\nChúc mừng bạn đã nâng cấp tài khoản lên gói Premium!\n\nBây giờ bạn có thể truy cập toàn bộ bài tập Cambridge Flyers, Movers, Starters và tất cả bài học cao cấp trên luyenthi.io.vn.\n\nChúc học sinh của bạn học vui và đạt kết quả tốt!\n\nĐội ngũ luyenthi.io.vn',
    mode: 'plain',
  },
  {
    name: '🔥 Nhắc nhở học bài',
    subject: 'Đừng để streak bị gián đoạn! 🔥',
    body: 'Chào {{name}},\n\nHọc sinh của bạn đã không luyện tập trong 3 ngày qua.\n\nChỉ cần 10-15 phút mỗi ngày để duy trì streak và tiến bộ liên tục!\n\n→ Truy cập: https://luyenthi.io.vn\n\nĐội ngũ luyenthi.io.vn',
    mode: 'plain',
  },
  {
    name: '📢 Tính năng mới',
    subject: 'Tính năng mới trên luyenthi.io.vn 🎊',
    body: '<p>Chào <strong>{{name}}</strong>,</p>\n<p>Chúng tôi vừa ra mắt tính năng mới trên <strong>luyenthi.io.vn</strong>!</p>\n<ul>\n<li>✅ Bài tập Cambridge Flyers Part 4 mới</li>\n<li>✅ Cải thiện giao diện học tập</li>\n<li>✅ Thêm 50+ bài học mới</li>\n</ul>\n<p><a href="https://luyenthi.io.vn">→ Trải nghiệm ngay</a></p>\n<p>Đội ngũ luyenthi.io.vn</p>',
    mode: 'html',
  },
];

function createStarters(): EmailTemplate[] {
  const now = Date.now();
  return STARTER_TEMPLATES.map((t, i) => ({
    ...t,
    id: `starter-${i}-${now}`,
    createdAt: now - i * 1000,
    updatedAt: now - i * 1000,
  }));
}

// ─── Template Card ────────────────────────────────────────────────────────────

function TemplateCard({
  template,
  onEdit,
  onDelete,
  onUse,
}: {
  template: EmailTemplate;
  onEdit: () => void;
  onDelete: () => void;
  onUse: () => void;
}) {
  return (
    <div
      className="card"
      style={{
        padding: '18px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        cursor: 'default',
        transition: 'box-shadow 0.15s ease',
      }}
      onMouseEnter={e => (e.currentTarget.style.boxShadow = 'var(--shadow-md)')}
      onMouseLeave={e => (e.currentTarget.style.boxShadow = '')}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-1)', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {template.name}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            📧 {template.subject}
          </div>
        </div>
        <span className={`badge badge-${template.mode === 'html' ? 'info' : 'free'}`} style={{ flexShrink: 0 }}>
          {template.mode.toUpperCase()}
        </span>
      </div>

      {/* Body preview */}
      <div style={{
        fontSize: 12.5,
        color: 'var(--text-3)',
        lineHeight: 1.55,
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical' as const,
        background: 'var(--bg-elevated)',
        padding: '8px 12px',
        borderRadius: 'var(--radius-sm)',
        fontFamily: template.mode === 'html' ? 'monospace' : 'inherit',
      }}>
        {template.body.replace(/<[^>]*>/g, '')}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn btn-ghost btn-sm" onClick={onDelete} style={{ color: 'var(--danger)' }}>🗑</button>
        <button className="btn btn-secondary btn-sm" onClick={onEdit}>✏️ Sửa</button>
        <button className="btn btn-primary btn-sm" onClick={onUse}>📤 Dùng template</button>
      </div>
    </div>
  );
}

// ─── Edit Modal ────────────────────────────────────────────────────────────────

interface TemplateForm {
  name: string;
  subject: string;
  body: string;
  mode: 'plain' | 'html';
}

const EMPTY_FORM: TemplateForm = { name: '', subject: '', body: '', mode: 'plain' };

// ─── Page ─────────────────────────────────────────────────────────────────────

export function EmailTemplatesPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<EmailTemplate[]>(() => {
    const loaded = loadTemplates();
    if (loaded.length === 0) {
      const starters = createStarters();
      saveTemplates(starters);
      return starters;
    }
    return loaded;
  });

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TemplateForm>(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  function persistTemplates(updated: EmailTemplate[]) {
    setTemplates(updated);
    saveTemplates(updated);
  }

  function openNew() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowModal(true);
  }

  function openEdit(template: EmailTemplate) {
    setEditingId(template.id);
    setForm({ name: template.name, subject: template.subject, body: template.body, mode: template.mode });
    setFormError('');
    setShowModal(true);
  }

  function handleDelete(id: string) {
    if (!confirm('Xóa template này?')) return;
    persistTemplates(templates.filter(t => t.id !== id));
    toast('Đã xóa template', 'success');
  }

  function handleSave() {
    if (!form.name.trim()) { setFormError('Tên template là bắt buộc'); return; }
    if (!form.subject.trim()) { setFormError('Subject là bắt buộc'); return; }
    if (!form.body.trim()) { setFormError('Body là bắt buộc'); return; }

    const now = Date.now();
    if (editingId) {
      persistTemplates(templates.map(t => t.id === editingId ? { ...t, ...form, updatedAt: now } : t));
      toast('Đã cập nhật template', 'success');
    } else {
      const newT: EmailTemplate = { id: `t-${now}`, ...form, createdAt: now, updatedAt: now };
      persistTemplates([newT, ...templates]);
      toast('Đã tạo template mới', 'success');
    }
    setShowModal(false);
  }

  function handleUse(template: EmailTemplate) {
    // Store in sessionStorage for Notifications page to pick up
    sessionStorage.setItem('email-draft', JSON.stringify({
      subject: template.subject,
      body: template.body,
      mode: template.mode,
    }));
    navigate('/admin/notifications');
    toast(`Đã tải template "${template.name}" vào form soạn thảo`, 'info');
  }

  function resetToStarters() {
    if (!confirm('Reset về starter templates? Templates hiện tại sẽ bị xóa.')) return;
    const starters = createStarters();
    persistTemplates(starters);
    toast('Đã reset về starter templates', 'success');
  }

  const filtered = templates.filter(t =>
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <div className="page-header">
        <div className="page-header__left">
          <h1>Email Templates</h1>
          <p>{templates.length} templates · lưu local</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={resetToStarters}>↺ Reset</button>
          <button className="btn btn-primary" onClick={openNew}>+ Template mới</button>
        </div>
      </div>

      {/* Search */}
      <div style={{ marginBottom: 20 }}>
        <div className="search-input-wrap">
          <span className="search-input-icon">🔍</span>
          <input
            type="search"
            className="search-input"
            placeholder="Tìm tên, subject..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Info box */}
      <div className="info-box" style={{ marginBottom: 20 }}>
        💡 Templates được lưu trong <strong>localStorage</strong>. Nhấn <strong>"Dùng template"</strong> để tự động điền vào form soạn email ở trang Thông báo.
        Biến hỗ trợ: <code>{'{{name}}'}</code>, <code>{'{{email}}'}</code>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__icon">📨</div>
          <div className="empty-state__text">Không tìm thấy template nào</div>
          <button className="btn btn-primary" onClick={openNew} style={{ marginTop: 12 }}>+ Tạo template đầu tiên</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {filtered.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              onEdit={() => openEdit(template)}
              onDelete={() => handleDelete(template.id)}
              onUse={() => handleUse(template)}
            />
          ))}
        </div>
      )}

      {/* Edit/Create Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingId ? '✏️ Sửa Template' : '+ Template mới'}
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Huỷ</button>
            <button className="btn btn-primary" onClick={handleSave}>
              {editingId ? 'Cập nhật' : 'Tạo template'}
            </button>
          </>
        }
      >
        {formError && (
          <div style={{ marginBottom: 12, color: 'var(--danger)', fontSize: 13, padding: '8px 12px', background: 'var(--danger-soft)', borderRadius: 6 }}>
            ⚠️ {formError}
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Tên template <span style={{ color: 'var(--danger)' }}>*</span></label>
          <input
            className="input"
            placeholder="vd: 🎉 Chào mừng Premium"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            style={{ width: '100%' }}
          />
        </div>

        <div className="form-group" style={{ marginTop: 12 }}>
          <label className="form-label">Subject email <span style={{ color: 'var(--danger)' }}>*</span></label>
          <input
            className="input"
            placeholder="Tiêu đề email..."
            value={form.subject}
            onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
            style={{ width: '100%' }}
          />
        </div>

        <div className="form-group" style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <label className="form-label" style={{ margin: 0 }}>Nội dung <span style={{ color: 'var(--danger)' }}>*</span></label>
            <div style={{ display: 'flex', gap: 2, background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', padding: 2 }}>
              {(['plain', 'html'] as const).map(mode => (
                <button
                  key={mode}
                  onClick={() => setForm(f => ({ ...f, mode }))}
                  style={{
                    padding: '3px 10px', borderRadius: 'var(--radius-sm)', border: 'none',
                    cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-sans)',
                    background: form.mode === mode ? 'var(--accent)' : 'transparent',
                    color: form.mode === mode ? '#fff' : 'var(--text-3)',
                  }}
                >
                  {mode === 'plain' ? 'Plain Text' : 'HTML'}
                </button>
              ))}
            </div>
          </div>
          <textarea
            className="input"
            style={{
              minHeight: 180, resize: 'vertical', width: '100%',
              fontFamily: form.mode === 'html' ? 'var(--font-mono)' : 'var(--font-sans)',
              fontSize: form.mode === 'html' ? 12.5 : 14,
            }}
            placeholder={form.mode === 'plain'
              ? 'Nội dung plain text. Dùng {{name}} cho tên người dùng.'
              : '<p>Nội dung HTML. Dùng <strong>{{name}}</strong> cho tên.</p>'}
            value={form.body}
            onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
          />
        </div>
      </Modal>
    </div>
  );
}
